/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import { getAssetPath } from '../../utils/assetPath'

interface IconProps {
  iconId?: string
  iconPath: string // relative to CDN root, e.g. 'assets/images/icons/forms/icon_search.svg'
  viewBox?: string
  className?: string
}

/**
 * Generic Icon component for loading external SVGs by URL and rendering a symbol by id (if provided).
 * - Uses CDN asset path resolution (getAssetPath)
 * - Caches loaded SVGs in a static map for performance
 */
const svgCache: Record<string, Promise<Document>> = {}

const Icon: React.FC<IconProps> = ({ iconId, iconPath, viewBox = '0 0 24 24', className }) => {
  const [svgContent, setSvgContent] = useState<string>('')
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    const url = getAssetPath(iconPath)
    let cancelled = false

    const fetchSvg = () => {
      if (!svgCache[url]) {
        svgCache[url] = fetch(url)
          .then((resp) => resp.text())
          .then((text) => {
            const parser = new window.DOMParser()
            return parser.parseFromString(text, 'image/svg+xml')
          })
      }
      return svgCache[url]
    }

    fetchSvg()
      .then((doc) => {
        if (cancelled || !isMounted.current) return
        let el: Element | null = null
        if (iconId) {
          el = doc.getElementById(iconId)
        } else {
          el = doc.documentElement
        }
        setSvgContent(el ? el.innerHTML : '')
      })
      .catch(() => {
        if (isMounted.current) setSvgContent('')
      })

    return () => {
      cancelled = true
      isMounted.current = false
    }
  }, [iconId, iconPath])

  return (
    <svg
      role="img"
      viewBox={viewBox}
      className={['mcc-icon', className].filter(Boolean).join(' ')}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  )
}

export default Icon
